"use client"

import {isProjectFilterMatch, ProjectsFilter, type ProjectsArchiveFilter} from "@/app/util/projectsUtil";
import {deleteProject, generateProjectName, updateProject, updateRoadmap} from "@/app/data/actions";
import {documentKeys, planningKeys, roadmapKeys} from "@/hooks/queries/queryKeys";
import {queryClient} from "@/hooks/queries/queryClient";
import {ThreadExecutionInfo, ThreadProgress, ThreadProgressItem} from "@/pb/api/devplan/types/threads";
import {refreshChildDocument, refreshProjectDocument} from "@/hooks/queries/use-document";
import {PlanSimulation, PlanSimulation_Target, PlanSimulation_TargetType, Roadmap} from "@/pb/api/devplan/types/tasks";
import {StateCreator} from "zustand/vanilla";
import {DocumentEntity, ProjectDetails, ProjectEntity} from "@/pb/api/devplan/types/documents";
import {Group, GroupItem, GroupSection} from "@/pb/api/devplan/types/grouping";
import {GroupSectionCreateInput} from "@/app/components/projects/grouping/GroupTableContext";
import {isEditableSection} from "@/app/components/projects/projectsGroupUtils";
import {backlogSectionKey, nextSectionKey, nowSectionKey, projectsGroupId} from "@/app/data/common";
import {LucideIcon} from "@/pb/api/devplan/types/common";
import {arrayMove} from "@dnd-kit/sortable";
import {v4 as uuid} from "uuid";
import {getProjectsOrder, GroupState} from "@/hooks/queries/stores/groupingState";
import {GroupDragInfo} from "@/app/components/projects/grouping/groupingTypes";
import {
  projectDescriptionAPI,
  projectPhaseDatesAPI,
  attachmentAPI,
  childDocumentsAPI,
  companyAttachmentsAPI,
  createDocumentAPI,
  documentAPI,
  groupAPI,
  groupsAPI,
  ideaPromoteAPI,
  planningTargetAPI,
  projectArchiveStatusAPI,
  projectAttachmentsAPI,
  projectDocumentsAPI,
  projectsListAPI,
  projectsAPI,
  projectsViewSettingsAPI,
  runsAPI,
} from "@/app/apiPaths";
import {
  CreateEmptyDocumentRequest,
  CreateEmptyDocumentResponse,
  CreateProjectWithRequirementsRequest,
  CreateProjectWithRequirementsResponse,
  GenerateProjectNameResponse,
  GetAllProjectDocsResponse,
  GetChildDocsResponse,
  GetDocResponse,
  GetGroupResponse,
  GetProjectAttachmentsResponse,
  GetProjectsViewSettingsResponse,
  GetProjectsWithDocsResponse,
  GetRunsResponse,
  ListGroupsResponse,
  PromoteIdeaRequest,
  PromoteIdeaResponse,
  SetProjectArchiveStatusRequest,
  SetProjectArchiveStatusResponse,
  UpdateProjectDescriptionRequest,
  UpdateProjectDescriptionResponse,
  UpdateProjectPhaseDatesRequest,
  UpdateProjectPhaseDatesResponse,
  UpdateProjectsViewSettingsRequest,
  UpdateProjectsViewSettingsResponse,
  UpsertGroupRequest,
  UpsertGroupResponse
} from "@/pb/api/devplan/services/web/company";
import {AttachmentDetails} from "@/pb/api/devplan/types/attachments";
import {ProjectsViewSettings} from "@/pb/api/devplan/types/company";
import {toast} from "sonner";
import logger from "@/app/logger";
import {jr} from "@/hooks/queries/response-handler";
import {GetPlanSimulationsRequest, GetPlanSimulationsResponse} from "@/pb/api/devplan/services/agent/planning";
import {planTargetKey} from "@/hooks/queries/stores/util";
import {RunItem} from "@/pb/api/devplan/types/runs";

export type DocumentsState = {
  documents: Record<string, DocumentEntity>;
  setDocument(doc: DocumentEntity): void;
  getDocument(id: string): DocumentEntity | undefined;
  setDocumentWithRefresh(doc: DocumentEntity): void;
  createEmptyDocument(companyId: number, req: CreateEmptyDocumentRequest): Promise<string>
  setDocuments(docs: DocumentEntity[]): void;
  deleteDocument(id: string): void;
  fetchDocument(id: string, companyId: number): Promise<void>;
  fetchChildDocuments(companyId: number, parentId: string): Promise<string[]>;

  activeThreads: ThreadExecutionInfo[];
  updateActiveThreads(active: ThreadExecutionInfo[], deactivated: string[], fullSnapshot: boolean): void;

  threadProgress: Record<string, ThreadProgress>;
  updateThreadProgress(threadId: string, updated: ThreadProgress): void;
  removeThreadProgress(threadId: string): void;
  getThreadProgressItem(threadId: string, itemId: string): ThreadProgressItem | undefined,

  roadmaps: Record<string, Roadmap>

  setRoadmap(roadmap: Roadmap): void;
  syncRoadmapToDB(companyId: number, id: string): Promise<void>;
  updateRoadmap(companyId: number, id: string, store: boolean, updater: (r: Roadmap) => Roadmap | undefined): Promise<void>

  projects: Record<string, ProjectEntity>;
  projectDocs: Record<string, string[]>;
  fetchProjectDocs(companyId: number, projectId: string): Promise<void>;
  fetchProjectsWithDocs(companyId: number, archiveFilter?: ProjectsArchiveFilter): Promise<void>;
  /**
   * Fetch the projects-with-docs list and a specific group in parallel and
   * commit both into the store via a single `set` call, so consumers never
   * see one fresh and the other stale (which causes `groupInitializer` to
   * either phantom-delete projects or phantom-add them to Backlog).
   */
  refreshProjectsAndGroup(companyId: number, groupId: string, includeArchived?: boolean): Promise<void>;
  setProject(project: ProjectEntity): void;
  getProject(id: string): ProjectEntity | undefined;
  updateProjectDetails(id: string, updater: (p: ProjectDetails) => ProjectDetails | undefined): Promise<void>;
  updateProject(id: string, updater: (p: ProjectEntity) => ProjectEntity | undefined): Promise<void>;
  updateProjectPhaseDates(companyId: number, id: string, details: Partial<ProjectDetails>): Promise<void>;
  updateProjectDescription(companyId: number, id: string, description: string): Promise<void>;
  deleteProject(companyId: number, id: string): Promise<void>;
  setProjectArchived(companyId: number, id: string, isArchived: boolean): Promise<void>;
  generateProjectName(companyId: number, id: string): Promise<void>;
  createProjectWithRequirements(companyId: number, req: CreateProjectWithRequirementsRequest): Promise<string>;

  // Ideas (projects with kind='idea') - use project APIs
  promoteIdea(companyId: number, ideaId: string, projectTemplateId: string): Promise<{
    projectId: string,
    documentId: string,
    warning?: string,
  }>;

  groupViews: Record<string, GroupState>;
  groups: Record<string, Group>;
  fetchGroups(companyId: number): Promise<void>,
  fetchGroup(companyId: number, id: string): Promise<void>,
  getGroup(id: string): Group | undefined
  createGroupView(groupId: string, projectDocs: Record<string, DocumentEntity[]>): void
  addGroupRootSection(groupId: string, section: GroupSectionCreateInput): Promise<void>
  updateGroupSection(groupId: string, sectionKey: string, updates: { name?: string, icon?: LucideIcon }): Promise<void>
  deleteGroupSection(groupId: string, sectionKey: string): Promise<void>
  updateGroupSectionOrder(groupId: string, sectionKey: string, newIndex: number): Promise<void>
  updateGroup(groupId: string, updater: (g: Group) => Group | undefined, canUpdate: boolean): Promise<Group | undefined>
  upsertGroup(group: Group, canUpdate: boolean): Promise<void>
  updateGroupView(id: string, create: boolean): void
  updateGroupItemIds(
    {groupId, dragInfo, active, over}: {
      groupId: string,
      dragInfo: GroupDragInfo,
      active?: (keys: string[]) => string[],
      over?: (keys: string[]) => string[]
    }
  ): Promise<void>,

  planSimulations: Record<string, PlanSimulation>
  fetchPlanSimulations(companyId: number, targets: PlanSimulation_Target[]): Promise<void>

  projectsViewSettings: Record<string, ProjectsViewSettings>
  fetchProjectsViewSettings(companyId: number, viewId: string): Promise<void>
  getProjectsViewSettings(companyId: number, viewId: string): ProjectsViewSettings | undefined
  updateProjectsViewSettings(settings: ProjectsViewSettings): Promise<void>

  attachments: Record<string, AttachmentDetails>
  cacheAttachments(attachments: AttachmentDetails[]): void
  fetchAttachment(companyId: number, attachmentId: string): Promise<void>
  // Attachments - indexed by project ID
  projectAttachments: Record<string, string[]>
  fetchProjectAttachments(companyId: number, projectId: string): Promise<void>
  deleteAttachment(companyId: number, attachmentId: string, projectId: string): Promise<void>
  // Company-level attachments - indexed by company ID
  companyAttachments: Record<number, string[]>
  fetchCompanyAttachments(companyId: number): Promise<void>
  // Workspace integration attachments - indexed by company ID
  integrationAttachments: Record<number, string[]>
  fetchIntegrationAttachments(companyId: number): Promise<void>
  deleteCompanyAttachment(companyId: number, attachmentId: string): Promise<void>

  // Runs - indexed by entityId
  runs: Record<string, RunItem>
  fetchRuns(companyId: number): Promise<void>
};


export const createDocumentsSlice: StateCreator<
  DocumentsState,
  [],
  [],
  DocumentsState
> = ((set, get) => ({
  documents: {},
  setDocument: (doc: DocumentEntity) => set((s) => {
    return {...s, documents: {...s.documents, [doc.id]: doc}}
  }),
  setDocuments: (docs: DocumentEntity[]) => set((s) => {
    const copy = {...s.documents}
    docs.forEach(doc => copy[doc.id] = doc)
    return {...s, documents: copy}
  }),
  deleteDocument: (id: string) => set(s => {
    const {[id]: deleted, ...rest} = s.documents;
    return {...s, documents: rest}
  }),
  createEmptyDocument: (companyId, req) => fetch(createDocumentAPI(companyId),
    {method: "POST", body: JSON.stringify(CreateEmptyDocumentRequest.toJSON(req))}
  ).then(jr)
    .then(js => {
      const {document} = CreateEmptyDocumentResponse.fromJSON(js)
      get().setDocumentWithRefresh(document!)
      void get().fetchProjectDocs(companyId, document!.projectId)
      return document!.id
    }),
  setDocumentWithRefresh: (doc: DocumentEntity) => set((s) => {
    refreshProjectDocument(doc)
    refreshChildDocument(doc)
    return {...s, documents: {...s.documents, [doc.id]: doc}}
  }),
  fetchDocument: async (id, companyId) => {
    const resp = await fetch(documentAPI(companyId, id))
    if (!resp.ok) {
      return Promise.reject(resp.statusText)
    }
    const {document} = GetDocResponse.fromJSON(await resp.json())
    if (!document) {
      set(s => {
        const {[id]: deleted, ...rest} = s.documents;
        return {...s, documents: rest}
      })
      return
    }
    refreshProjectDocument(document)
    refreshChildDocument(document)
    set(s => ({...s, documents: {...s.documents, [id]: document}}))
  },
  fetchChildDocuments: async (companyId, parentId) => {
    const resp = await fetch(childDocumentsAPI(companyId, parentId))
    if (!resp.ok) {
      return Promise.reject(resp.statusText)
    }
    const {documents} = GetChildDocsResponse.fromJSON(await resp.json())
    documents.forEach(doc => refreshProjectDocument(doc))
    const queryKey = documentKeys.byParent(companyId, parentId)
    queryClient.setQueryData(queryKey, documents.map(d => d.id))
    set((s) => {
      const copy = {...s.documents}
      documents.forEach(doc => copy[doc.id] = doc)
      return {...s, documents: copy}
    })
    return documents.map(d => d.id)
  },

  activeThreads: [],
  updateActiveThreads: (activeThreads, deactivated, fullSnapshot) => set(s => {
    if (fullSnapshot) {
      const threadProgress = activeThreads.reduce((acc, t) => {
        if (t.thread?.id && t.progress) {
          acc[t.thread.id] = t.progress
        }
        return acc
      }, {} as Record<string, ThreadProgress>)
      return {...s, threadProgress, activeThreads}
    }
    setTimeout(() => {
      const st = get()
      deactivated.forEach(st.removeThreadProgress)
    }, 60000 * 5)
    const newData: ThreadExecutionInfo[] = s.activeThreads.filter(d => d.thread && !deactivated.includes(d.thread.id))
    newData.push(...activeThreads)
    const threadProgress = {...s.threadProgress}
    activeThreads.forEach(t => {
      if (t.thread?.id && t.progress) {
        threadProgress[t.thread.id] = t.progress
      }
    })
    return {...s, activeThreads: newData, threadProgress}
  }),
  threadProgress: {},
  updateThreadProgress: (threadId, updated) =>
    set(s => ({...s, threadProgress: {...s.threadProgress, [threadId]: updated}})),
  removeThreadProgress: (threadId) => set(s => {
    const active = s.activeThreads.find(t => t.thread?.id === threadId) !== undefined
    // we should not be removing data for active threads.
    if (active) return s
    const threadProgress = {...s.threadProgress}
    delete threadProgress[threadId]
    return {...s, threadProgress}
  }),
  getThreadProgressItem: (threadId, itemId) =>
    get().threadProgress[threadId]?.items.find(i => i.id === itemId),

  roadmaps: {},
  setRoadmap: (roadmap) => set(s => ({...s, roadmaps: {...s.roadmaps, [roadmap.id]: roadmap}})),
  syncRoadmapToDB: async (companyId, id) => {
    const roadmap = get().roadmaps[id]
    if (!roadmap) {
      return
    }
    await updateRoadmap(companyId, roadmap)
      .then(() => queryClient.invalidateQueries({queryKey: roadmapKeys.all(companyId)}))
  },
  updateRoadmap: async (companyId, id, store, updater) => {
    const roadmap = get().roadmaps[id]
    if (!roadmap) {
      return
    }
    const updated = updater(roadmap)
    if (!updated) {
      return
    }
    get().setRoadmap(updated)
    if (store) {
      await get().syncRoadmapToDB(companyId, id)
    }
  },
  projects: {},
  projectDocs: {},
  fetchProjectDocs: async (companyId, projectId) => fetch(projectDocumentsAPI(companyId, projectId))
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
        const {documents} = GetAllProjectDocsResponse.fromJSON(js)
        get().setDocuments(documents)
        documents.forEach(refreshIndividualDoc)
        set(s => ({...s, projectDocs: {...s.projectDocs, [projectId]: documents.map(d => d.id)}}))
      }
    ),
  fetchProjectsWithDocs: async (companyId, archiveFilter = "active") => {
    const url = projectsListAPI(companyId, archiveFilter)
    return fetch(url)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`Failed to fetch projects: ${r.statusText}`)))
      .then(js => {
        const {projects} = GetProjectsWithDocsResponse.fromJSON(js)
        const documents = projects.flatMap(p => p.docs)
        get().setDocuments(documents)
        documents.forEach(refreshIndividualDoc)
        projects.forEach(p => get().setProject(p.project!))
        const projectDocs = projects.reduce(
          (a, d) => ({...a, [d.project!.id]: d.docs.map(d => d.id)}),
          {} as Record<string, string[]>)
        set(s => ({...s, projectDocs: {...s.projectDocs, ...projectDocs}}))
      })
  },
  refreshProjectsAndGroup: async (companyId, groupId, includeArchived = true) => {
    const projectsUrl = projectsListAPI(companyId, includeArchived ? "all" : "active")
    const [groupResp, projectsResp] = await Promise.all([
      fetch(groupAPI(companyId, groupId)),
      fetch(projectsUrl),
    ])
    if (!groupResp.ok) {
      throw new Error(`Failed to fetch group: ${groupResp.statusText}`)
    }
    if (!projectsResp.ok) {
      throw new Error(`Failed to fetch projects: ${projectsResp.statusText}`)
    }
    const [groupJson, projectsJson] = await Promise.all([groupResp.json(), projectsResp.json()])
    const {group} = GetGroupResponse.fromJSON(groupJson)
    const {projects: pwds} = GetProjectsWithDocsResponse.fromJSON(projectsJson)
    const documents = pwds.flatMap(p => p.docs)
    documents.forEach(refreshIndividualDoc)
    set(s => {
      const docsCopy = {...s.documents}
      documents.forEach(d => { docsCopy[d.id] = d })
      const projectsCopy = {...s.projects}
      pwds.forEach(p => {
        if (p.project) projectsCopy[p.project.id] = p.project
      })
      const projectDocs = pwds.reduce(
        (a, d) => ({...a, [d.project!.id]: d.docs.map(doc => doc.id)}),
        {} as Record<string, string[]>,
      )
      const next: DocumentsState = {
        ...s,
        documents: docsCopy,
        projects: projectsCopy,
        projectDocs: {...s.projectDocs, ...projectDocs},
      }
      if (group) {
        return updateGroupView({...next, groups: {...next.groups, [group.id]: group}}, group.id, false)
      }
      return next
    })
  },
  setProject: (project) => set(s => ({...s, projects: {...s.projects, [project.id]: project}})),
  updateProjectDetails: async (id, updater) => get().updateProject(id, project => {
    const updated = updater(project.details!)
    return updated ? {...project, details: updated} : undefined
  }),
  updateProject: async (id, updater) => {
    const project = get().projects[id]
    if (!project) {
      return
    }
    const updated = updater(project)
    if (!updated) {
      return
    }
    get().setProject(await updateProject(updated))
  },
  updateProjectPhaseDates: async (companyId, id, phaseDates) => {
    const project = get().projects[id]
    if (!project) return
    const details = {...(project.details ?? {}), ...phaseDates} as ProjectDetails
    return fetch(projectPhaseDatesAPI(companyId, id), {
      method: "PATCH",
      body: JSON.stringify(UpdateProjectPhaseDatesRequest.toJSON({details})),
    }).then(jr).then(js => {
      const {project: updated} = UpdateProjectPhaseDatesResponse.fromJSON(js)
      if (updated) {
        set(s => ({...s, projects: {...s.projects, [id]: updated}}))
      }
    })
  },
  updateProjectDescription: async (companyId, id, description) => {
    return fetch(projectDescriptionAPI(companyId, id), {
      method: "PATCH",
      body: JSON.stringify(UpdateProjectDescriptionRequest.toJSON({description})),
    }).then(jr).then(js => {
      const {project: updated} = UpdateProjectDescriptionResponse.fromJSON(js)
      if (updated) {
        set(s => ({...s, projects: {...s.projects, [id]: updated}}))
      }
    })
  },
  deleteProject: async (companyId, id) => deleteProject(id, companyId)
    .then(() => set(s => {
      const {[id]: _, ...projects} = s.projects
      return {...s, projects}
    })),
  setProjectArchived: async (companyId, id, isArchived) => fetch(projectArchiveStatusAPI(companyId, id), {
    method: "POST",
    body: JSON.stringify(SetProjectArchiveStatusRequest.toJSON({isArchived})),
  }).then(jr).then(js => {
    const {project} = SetProjectArchiveStatusResponse.fromJSON(js)
    if (project) {
      set(s => ({...s, projects: {...s.projects, [id]: project}}))
    }
  }),
  generateProjectName: async (companyId, id) => generateProjectName(companyId, id)
    .then(js => {
      const {project} = GenerateProjectNameResponse.fromJSON(js)
      if (project) {
        set(s => ({...s, projects: {...s.projects, [id]: project}}))
      }
    }),
  createProjectWithRequirements: async (companyId, req) => fetch(projectsAPI(companyId),
    {method: "POST", body: JSON.stringify(CreateProjectWithRequirementsRequest.toJSON(req))})
    .then(jr)
    .then(async js => {
      const {projectId, project} = CreateProjectWithRequirementsResponse.fromJSON(js)
      if (project) {
        get().setProject(project)
      }
      await get().fetchProjectDocs(companyId, projectId)

      refetchWithLimit(0, projectId, companyId, get)
      return projectId
    })
  ,
  getProject: id => get().projects[id],

  // Ideas (projects with kind='idea') - use project APIs
  promoteIdea: async (companyId, ideaId, projectTemplateId) => {
    const resp = await fetch(ideaPromoteAPI(companyId, ideaId), {
      method: "POST",
      body: JSON.stringify(PromoteIdeaRequest.toJSON({projectTemplateId})),
    })
    if (!resp.ok) {
      throw new Error(`Failed to promote idea: ${resp.statusText}`)
    }
    const json = await resp.json()
    const {document, warning} = PromoteIdeaResponse.fromJSON(json)
    if (!document) {
      throw new Error("Failed to promote idea: no document returned")
    }
    const projectId = document.projectId
    const documentId = document.id
    // Remove the old idea from the store since its kind changed
    set(s => {
      const {[ideaId]: _, ...projects} = s.projects
      return {...s, projects}
    })
    // Fetch the promoted project
    await get().fetchProjectDocs(companyId, projectId)
    return {projectId, documentId, warning}
  },
  getDocument: id => get().documents[id],

  groupViews: {},
  groups: {},
  fetchGroups: async (companyId) => fetch(groupsAPI(companyId))
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {groups} = ListGroupsResponse.fromJSON(js);
      const updates = groups.reduce((a, g) => ({...a, [g.id]: g}), {} as Record<string, Group>)
      set(s => ({...s, groups: {...s.groups, ...updates}}))
    }),
  fetchGroup: async (companyId, id) => fetch(groupAPI(companyId, id))
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {group} = GetGroupResponse.fromJSON(js);
      logger.debug({js}, "Fetched group")
      if (group) {
        set(s => updateGroupView({...s, groups: {...s.groups, [group.id]: group}}, group.id, false))
      } else {
        set(s => {
          const {[id]: deletedGroup, ...restGroups} = s.groups;
          const {[id]: deletedView, ...restGroupViews} = s.groupViews;
          return {...s, groups: restGroups, groupViews: restGroupViews}
        })
      }
    }),
  getGroup: id => get().groups[id],
  createGroupView: (groupId, projectDocs) => {
    const group = get().groups[groupId]
    if (!group) {
      return
    }
    const groupState = new GroupState(group, projectDocs)
    return set(s => ({...s, groupViews: {...s.groupViews, [group.id]: groupState}}))
  },
  updateGroup: async (groupId, updater, canUpdate) => {
    const group = get().groups[groupId]
    if (!group) {
      return undefined
    }
    const updated = updater(group)
    if (updated) {
      await get().upsertGroup(updated, canUpdate)
    }
    return get().groups[groupId]
  },
  upsertGroup: async (group, canUpdate) => {
    set(s => updateGroupView({...s, groups: {...s.groups, [group.id]: group}}, group.id, false))
    if (!canUpdate) {
      logger.debug({group}, "No update allowed")
      toast.warning("You can't update the roadmap view")
      return
    }
    logger.debug({group}, "Upserting group")
    // Persist to backend
    const {companyId} = group
    await fetch(groupsAPI(companyId), {method: 'POST', body: JSON.stringify(UpsertGroupRequest.toJSON({group}))})
      .then(async r => {
        if (r.status === 429) {
          logger.info({groupId: group.id, response: r}, "Revision mismatch detected, reloading latest version")
          toast.error("Update failed because your data is outdated. Reloading latest version.");
          await get().fetchGroup(companyId, group.id);
          throw new Error("Revision mismatch");
        }
        return r.ok ? r.json() : Promise.reject(r.statusText)
      })
      .catch(e => {
        if (e.message === "Revision mismatch") {
          // Already handled
          return
        }
        toast.error(`Failed to update group: ${e}`)
        throw e
      })
      .then(js => {
        if (!js) return // Skip if no response (happens after revision mismatch)
        const {group: updated} = UpsertGroupResponse.fromJSON(js)
        if (updated) {
          set(s => updateGroupView({...s, groups: {...s.groups, [updated.id]: updated}}, updated.id, false))
        }
      })
      .finally(async () => {
        const {companyId} = group
        if (group.id === projectsGroupId(group.companyId)) {
          // Small delay to ensure backend has finished processing and cache invalidation
          await new Promise(resolve => setTimeout(resolve, 250))
          // Reset queries completely to clear any cached data
          await queryClient.resetQueries({
            queryKey: planningKeys.all(companyId)
          })
        }
      })
  },
  updateGroupView: (id, create) => set(s => updateGroupView(s, id, create)),
  addGroupRootSection: async (groupId, section) => {
    const {rootOnly, name, icon} = section
    const group = get().groups[groupId]
    if (!group) {
      return Promise.reject("Group not found")
    }

    // Check for duplicate section names
    const existingSections = group.items.filter(i => i.type?.$case === "section")
    const duplicateName = existingSections.some(s =>
      s.type?.$case === "section" && s.type.value.name.toLowerCase() === name.trim().toLowerCase()
    )
    if (duplicateName) {
      const err = new Error("Section name already exists")
      err.name = "SectionNameExistsError"
      return Promise.reject(err)
    }

    const newSectionKey = uuid()
    const newSection = GroupItem.create(
      {
        key: newSectionKey, entityId: newSectionKey, rootOnly: rootOnly,
        type: {$case: "section", value: GroupSection.create({name, icon})},
        noDrag: false, // Allow custom sections to be draggable for reordering
        noDrop: false
      }
    )
    return get().updateGroup(groupId, (group) => {
      if (!group) {
        return undefined
      }
      return {
        ...group,
        items: [...group.items, newSection],
        topLevelItems: [newSection.key, ...group.topLevelItems]
      }
    }, true).then(() => undefined)
  },
  updateGroupSection: async (groupId: string, sectionKey: string, updates: { name?: string, icon?: LucideIcon }) => {
    if (!isEditableSection(sectionKey)) {
      return Promise.reject("Cannot update this section")
    }

    const group = get().groups[groupId]
    if (!group) {
      return Promise.reject("Group not found")
    }

    // Check for duplicate section names (excluding the current section)
    if (updates.name) {
      const existingSections = group.items.filter(
        i => i.type?.$case === "section" && i.key !== sectionKey
      )
      const duplicateName = existingSections.some(s =>
        s.type?.$case === "section" && s.type.value.name.toLowerCase() === updates.name!.trim().toLowerCase()
      )
      if (duplicateName) {
        const err = new Error("Section name already exists")
        err.name = "SectionNameExistsError"
        return Promise.reject(err)
      }
    }

    return get().updateGroup(groupId, (group) => {
      if (!group) {
        return undefined
      }
      const sectionItem = group.items.find(i => i.key === sectionKey)
      if (!sectionItem || sectionItem.type?.$case !== "section") {
        return undefined
      }
      const updatedSection = GroupSection.create({
        name: updates.name ?? sectionItem.type.value.name,
        icon: updates.icon ?? sectionItem.type.value.icon
      })
      const updatedItem = {
        ...sectionItem,
        type: {$case: "section" as const, value: updatedSection}
      }
      return {
        ...group,
        items: group.items.map(i => i.key === sectionKey ? updatedItem : i)
      }
    }, true).then(() => undefined)
  },
  deleteGroupSection: async (groupId: string, sectionKey: string) => {
    if (!isEditableSection(sectionKey)) {
      return Promise.reject("Cannot delete this section")
    }
    return get().updateGroup(groupId, (group) => {
      if (!group) {
        return undefined
      }
      const sectionItem = group.items.find(i => i.key === sectionKey)
      if (!sectionItem || sectionItem.type?.$case !== "section") {
        return undefined
      }
      // Move all projects from deleted section to Backlog
      const projectsToMove = sectionItem.childItems || []

      return {
        ...group,
        items: group.items
          .filter(i => i.key !== sectionKey) // Remove the deleted section
          .map(i => {
            if (i.key === backlogSectionKey) {
              // Add projects to backlog, avoiding duplicates
              const existingProjects = new Set(i.childItems)
              const newProjects = [...i.childItems, ...projectsToMove.filter(p => !existingProjects.has(p))]
              return {...i, childItems: newProjects}
            }
            return i
          }),
        topLevelItems: group.topLevelItems.filter(key => key !== sectionKey)
      }
    }, true).then(() => undefined)
  },
  updateGroupSectionOrder: async (groupId: string, sectionKey: string, newIndex: number) => {
    return get().updateGroup(groupId, (group) => {
      if (!group) {
        return undefined
      }
      const currentIndex = group.topLevelItems.indexOf(sectionKey)
      if (currentIndex === -1) {
        return undefined
      }
      if (currentIndex === newIndex) {
        return group // No change needed
      }
      return {
        ...group,
        topLevelItems: arrayMove(group.topLevelItems, currentIndex, newIndex)
      }
    }, true).then(() => undefined)
  },
  updateGroupItemIds: async ({groupId, dragInfo, active, over}) => {
    const groupView = get().groupViews[groupId]?.views?.project
    const group = get().groups[groupId]
    if (!groupView || !group) {
      return Promise.reject("Group is not found")
    }
    const {activeItemKey, activeSectionKey, overSectionKey} = dragInfo
    const activeEntry = groupView.entryByKey[activeItemKey]
    const activeItem = activeEntry ? groupView.itemByKey[activeEntry.key] : undefined
    if (activeItem?.rootOnly && activeItemKey !== overSectionKey) {
      return
    }
    // Get current childItems from group.items (which reflects latest local state)
    const getChildItems = (sectionKey: string) =>
      group.items.find(i => i.key === sectionKey)?.childItems ?? []
    const activeChildKeys = (active && activeSectionKey) ? active(getChildItems(activeSectionKey)) : undefined
    const overChildKeys = (over && overSectionKey) ? over(getChildItems(overSectionKey)) : undefined
    const updated = {...group, items: [...group.items]}
    updated.items = updated.items.map(i => {
      if (activeChildKeys && i.key === activeSectionKey) {
        return {...i, childItems: activeChildKeys}
      }
      if (overChildKeys && i.key === overSectionKey) {
        return {...i, childItems: overChildKeys}
      }
      return i
    })
    logger.debug({updated, dragInfo}, "Updated group")
    await get().upsertGroup(updated, true)
  },
  planSimulations: {},
  fetchPlanSimulations: async (companyId, targets) => fetch(planningTargetAPI(companyId), {
    method: "POST", body: JSON.stringify(GetPlanSimulationsRequest.toJSON({companyId, targets}))
  }).then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {simulations} = GetPlanSimulationsResponse.fromJSON(js)
      set(st => {
        let planSimulations = {...st.planSimulations}
        for (const t of targets) {
          const {[planTargetKey(t)]: _, ...theRest} = planSimulations
          planSimulations = theRest
        }
        simulations.forEach(s => {
          if (s.target) {
            planSimulations[planTargetKey(s.target)] = s
          }
        })
        return {...st, planSimulations}
      })
    }),
  projectsViewSettings: {},
  fetchProjectsViewSettings: async (companyId, viewId) => fetch(projectsViewSettingsAPI(companyId, viewId))
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {settings} = GetProjectsViewSettingsResponse.fromJSON(js)
      if (settings) {
        set(s => ({
          ...s,
          projectsViewSettings: {...s.projectsViewSettings, [`${companyId}:${viewId}`]: settings}
        }))
      }
    }),
  getProjectsViewSettings: (companyId, viewId) => get().projectsViewSettings[`${companyId}:${viewId}`],
  updateProjectsViewSettings: async (settings) => {
    const {companyId, viewId} = settings
    set(s => ({
      ...s,
      projectsViewSettings: {...s.projectsViewSettings, [`${companyId}:${viewId}`]: settings}
    }))
    const request = UpdateProjectsViewSettingsRequest.create({settings})

    return fetch(projectsViewSettingsAPI(companyId, viewId), {
      method: 'PUT',
      body: JSON.stringify(UpdateProjectsViewSettingsRequest.toJSON(request)),
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
      .then(js => {
        const {settings: updated} = UpdateProjectsViewSettingsResponse.fromJSON(js)
        if (updated) {
          set(s => ({
            ...s,
            projectsViewSettings: {...s.projectsViewSettings, [`${companyId}:${viewId}`]: updated}
          }))
        }
      })
  },

  attachments: {},
  cacheAttachments: (attachments) => {
    if (attachments.length === 0) return
    const updated = attachments.reduce((a, c) => ({...a, [c.id]: c}), {} as Record<string, AttachmentDetails>)
    set(s => ({
      ...s,
      attachments: {...s.attachments, ...updated},
    }))
  },
  fetchAttachment: async (companyId, attachmentId) => {
    if (get().attachments[attachmentId]) return
    const js = await fetch(attachmentAPI(companyId, attachmentId)).then(jr)
    const attachment = AttachmentDetails.fromJSON(js)
    set(s => ({
      ...s,
      attachments: {...s.attachments, [attachment.id]: attachment},
    }))
  },
  projectAttachments: {},
  fetchProjectAttachments: (companyId, projectId) => fetch(projectAttachmentsAPI(companyId, projectId))
    .then(jr)
    .then(js => {
      const {attachments} = GetProjectAttachmentsResponse.fromJSON(js)
      const updated = (attachments || []).reduce((a, c) => ({...a, [c.id]: c}), {} as Record<string, AttachmentDetails>)
      const sorted = (attachments || []).sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
        return dateB - dateA
      })
      set(s => ({
        ...s,
        attachments: {...s.attachments, ...updated},
        projectAttachments: {...s.projectAttachments, [projectId]: sorted.map(a => a.id)},
      }))
    }),
  deleteAttachment: (companyId, attachmentId, projectId) => fetch(attachmentAPI(companyId, attachmentId), {
    method: "DELETE",
  }).then(jr).then(() => {
    set(s => ({
      ...s,
      projectAttachments: {
        ...s.projectAttachments,
        [projectId]: (s.projectAttachments[projectId] || []).filter(a => a !== attachmentId)
      }
    }))
  }),
  companyAttachments: {},
  fetchCompanyAttachments: (companyId) => fetch(companyAttachmentsAPI(companyId))
    .then(jr)
    .then(js => {
      const {attachments} = GetProjectAttachmentsResponse.fromJSON(js)
      const updated = (attachments || []).reduce((a, c) => ({...a, [c.id]: c}), {} as Record<string, AttachmentDetails>)
      const sorted = (attachments || []).sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
        return dateB - dateA
      })
      set(s => ({
        ...s,
        attachments: {...s.attachments, ...updated},
        companyAttachments: {...s.companyAttachments, [companyId]: sorted.map(a => a.id)},
      }))
    }),
  integrationAttachments: {},
  fetchIntegrationAttachments: (companyId) => fetch(`${companyAttachmentsAPI(companyId)}?hasIntegrationBucket=true`)
    .then(jr)
    .then(js => {
      const {attachments} = GetProjectAttachmentsResponse.fromJSON(js)
      const updated = (attachments || []).reduce((a, c) => ({...a, [c.id]: c}), {} as Record<string, AttachmentDetails>)
      const sorted = (attachments || []).sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
        return dateB - dateA
      })
      set(s => ({
        ...s,
        attachments: {...s.attachments, ...updated},
        integrationAttachments: {...s.integrationAttachments, [companyId]: sorted.map(a => a.id)},
      }))
    }),
  deleteCompanyAttachment: async (companyId, attachmentId) => {
    // Optimistic removal
    const prev = get().companyAttachments[companyId] || []
    const prevIntegrationAttachments = get().integrationAttachments
    const nextIntegrationAttachments = Object.fromEntries(
      Object.entries(prevIntegrationAttachments).map(([key, ids]) => [key, ids.filter(a => a !== attachmentId)])
    )
    set(s => ({
      ...s,
      companyAttachments: {
        ...s.companyAttachments,
        [companyId]: prev.filter(a => a !== attachmentId)
      },
      integrationAttachments: {...s.integrationAttachments, ...nextIntegrationAttachments},
    }))
    try {
      await fetch(attachmentAPI(companyId, attachmentId), {method: "DELETE"}).then(jr)
    } catch (err) {
      // Restore on failure
      set(s => ({
        ...s,
        companyAttachments: {
          ...s.companyAttachments,
          [companyId]: prev
        },
        integrationAttachments: prevIntegrationAttachments,
      }))
      throw err
    }
  },

  // Runs
  runs: {},
  fetchRuns: (companyId) => fetch(runsAPI(companyId))
    .then(jr)
    .then(js => {
      const {runs, tasks, projects, features} = GetRunsResponse.fromJSON(js)
      const runsMap = runs.reduce((acc, run) => ({...acc, [run.entityId]: run}), {} as Record<string, RunItem>)
      const tasksMap = tasks.reduce((acc, task) => ({...acc, [task.id]: task}), {} as Record<string, DocumentEntity>)
      const featuresMap = features.reduce((acc, feature) => ({
        ...acc,
        [feature.id]: feature
      }), {} as Record<string, DocumentEntity>)
      const projectsMap = projects.reduce((acc, proj) => ({
        ...acc,
        [proj.id]: proj
      }), {} as Record<string, ProjectEntity>)
      set(s => ({
        ...s,
        runs: {...s.runs, ...runsMap},
        documents: {...s.documents, ...tasksMap, ...featuresMap},
        projects: {...s.projects, ...projectsMap},
      }))
    }),
}))

export function getCachedDocumentsByIds(state: DocumentsState, documentIds: string[] | undefined) {
  if (documentIds === undefined) {
    return undefined
  }
  const result: DocumentEntity[] = []
  documentIds?.sort().forEach(id => {
    const doc = id in state.documents ? state.documents[id] : null
    if (doc) {
      result.push(doc)
    }
  })
  return result
}

export function getCachedProjectsByIds(
  state: DocumentsState,
  projectsIds: string[] | undefined,
  filter?: ProjectsFilter,
) {
  const result: ProjectEntity[] = []
  projectsIds?.sort().forEach(id => {
    const project = id in state.projects ? state.projects[id] : null
    if (!project || !isProjectFilterMatch(project, filter)) {
      return
    }
    result.push(project)
  })
  return result
}

export function getCachedRoadmapsByIds(state: DocumentsState, roadmapIds: string[] | undefined) {
  const result: Roadmap[] = []
  roadmapIds?.sort().forEach(id => {
    const roadmap = id in state.roadmaps ? state.roadmaps[id] : null
    if (roadmap) {
      result.push(roadmap)
    }
    return result
  })
  return result
}

function refreshIndividualDoc(doc: DocumentEntity) {
  const queryKey = documentKeys.detail(doc.companyId, doc.id)
  queryClient.setQueryData(queryKey, doc)
}

function updateGroupView(st: DocumentsState, id: string, create: boolean): DocumentsState {
  const group = st.groups[id]
  const groupView = st.groupViews[id]
  if (!group || (!create && !groupView) || (create && groupView)) {
    return st
  }
  logger.debug({group, groupView}, "updateGroupView")
  const itemByKey = group.items.reduce((a, i) => ({...a, [i.key]: i}), {} as Record<string, GroupItem>)
  const projectIds = getProjectsOrder(group, itemByKey)
  const projectDocs: Record<string, DocumentEntity[]> = {}
  Object.values(st.documents).forEach(d => {
    const {projectId} = d
    if (projectIds.includes(d.projectId)) {
      projectDocs[projectId] = [...(projectDocs[projectId] || []), d]
    }
  })
  const groupState = new GroupState(group, projectDocs)
  return {...st, groupViews: {...st.groupViews, [group.id]: groupState}}
}

export async function invalidateProjectGroupSim(companyId: number, sectionKey: string) {
  await queryClient.invalidateQueries({
    queryKey: planningKeys.target(companyId, {id: sectionKey, type: PlanSimulation_TargetType.PROJECT_GROUP_ITEM})
  })
}

function refetchWithLimit(limit: number, projectId: string, companyId: number, getter: () => DocumentsState) {
  setTimeout(() => {
    const prj = getter().projects[projectId]
    // trying to fetch a project's title if it is not in the cache yet.
    if ((prj?.title.length ?? 0) > 0) return
    getter().fetchProjectsWithDocs(companyId).catch(err => {
      logger.error({err}, 'Failed to refresh project after title generation')
    })
    if (limit >= 10) {
      return
    }
    refetchWithLimit(limit + 1, projectId, companyId, getter)
  }, 1000)
}
